import React, { useState, useRef, useEffect } from 'react'
import { useAuth } from '../../../../app/modules/auth'
import { KTSVG } from '../../../helpers'
import Swal from 'sweetalert2'

const HeaderUserMenu = () => {
  const { currentUser, logout } = useAuth()
  const [showUserMenu, setShowUserMenu] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  const handleLogout = async (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setShowUserMenu(false)
    
    // Show confirmation popup
    const result = await Swal.fire({
      title: 'Confirm Sign Out',
      text: 'Are you sure you want to sign out from your account?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#ff7b00ff',
      cancelButtonColor: '#7e8299',
      confirmButtonText: '<i class="fas fa-sign-out-alt me-2"></i>Yes, Sign Out',
      cancelButtonText: '<i class="fas fa-times me-2"></i>Cancel',
      buttonsStyling: true,
      customClass: {
        confirmButton: 'btn btn-danger fw-semibold',
        cancelButton: 'btn btn-light-secondary fw-semibold',
        popup: 'rounded-3',
        title: 'fs-4 fw-bold text-dark',
        htmlContainer: 'fs-6 text-muted'
      },
      background: '#ffffff',
      backdrop: 'rgba(0, 0, 0, 0.4)',
      allowOutsideClick: false,
      allowEscapeKey: true,
      focusConfirm: false,
      reverseButtons: true
    })

    // Only logout if user confirmed
    if (result.isConfirmed) {
      logout()
      
      // Optional: Show success message
      Swal.fire({
        title: 'Signed Out Successfully',
        text: 'You have been logged out of your account.',
        icon: 'success',
        timer: 2000,
        showConfirmButton: false,
        customClass: {
          popup: 'rounded-3',
          title: 'fs-5 fw-bold text-success',
          htmlContainer: 'fs-6 text-muted'
        },
        background: '#ffffff',
        backdrop: 'rgba(0, 0, 0, 0.4)'
      })
    }
  }

  const getUserDisplayName = () => {
    return currentUser?.auth?.userName || currentUser?.username || 'User'
  }

  const getUserRole = () => {
    const role = currentUser?.auth?.role
    if (!role) return 'User'
    
    // Format role for display
    return role.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())
  }

  const getUserEmail = () => {
    return currentUser?.auth?.email || ''
  }

  const toggleUserMenu = (e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault()
      e.stopPropagation()
    }
    setShowUserMenu(!showUserMenu)
  }

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowUserMenu(false)
      }
    }

    if (showUserMenu) {
      document.addEventListener('mousedown', handleClickOutside)
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showUserMenu])

  return (
    <div className='d-flex align-items-stretch' ref={menuRef}>
      {/* User menu */}
      <div className='d-flex align-items-center ms-1 ms-lg-3 position-relative'>
        {/* User Avatar and Info */}
        <div 
          className='d-flex align-items-center px-3 py-2 cursor-pointer rounded-3 hover-bg-light transition-all'
          onClick={toggleUserMenu}
        >
          {/* Avatar */}
          <div className='symbol symbol-40px me-3'>
            <div className='symbol-label fs-2 fw-bold text-white bg-warning rounded-circle'>
              {getUserDisplayName().charAt(0).toUpperCase()}
            </div>
          </div>

          {/* User Info */}
          <div className='d-flex flex-column align-items-start justify-content-center d-none d-md-flex me-3'>
            <span className='text-dark fs-6 fw-bold mb-0'>
              {getUserDisplayName()}
            </span>
            <span className='text-muted fs-7 fw-medium'>
              {getUserRole()}
            </span>
          </div>

          {/* Dropdown arrow */}
          <div className='d-flex align-items-center'>
            <KTSVG
              path='/media/icons/duotune/arrows/arr072.svg'
              className={`svg-icon-5 text-muted transition-all duration-300 ${showUserMenu ? 'rotate-180' : ''}`}
            />
          </div>
        </div>

        {/* Simple logout dropdown */}
        {showUserMenu && (
          <div 
            className='position-absolute top-100 end-0 mt-3 bg-white rounded-3 shadow-lg border-0'
            style={{ 
              zIndex: 1050, 
              minWidth: '160px',
              boxShadow: '0 10px 30px rgba(0, 0, 0, 0.15)',
              animation: 'fadeInDown 0.3s ease'
            }}
          >
            {/* Logout button */}
            <div className='p-3'>
              <button
                type='button'
                className='btn btn-primary w-100 d-flex align-items-center justify-content-center fw-semibold text-white'
                onClick={handleLogout}
                style={{ borderRadius: '8px' }}
              >
                <KTSVG path='/media/icons/duotune/general/gen016.svg' className='svg-icon-5 me-2 text-white' />
                Sign Out
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export { HeaderUserMenu }

